// Sentry
import * as Sentry from '@sentry/react';

// GQL
import { client } from '../client';

// Queries
import { GET_ORDER_BY_ID } from '../queries/orders/orders';
import { warmupOrdersByIds } from './warmupOrders';

// Types
import { SurveillanceRoute } from '../../types/surveillanceRoute';

// ═══════════════════════════════════════════════════════════════════
// selectOrdersForSurveillanceRoutes
// ─────────────────────────────────────────────────────────────────
// Patrón canónico ROIS (ver `selectAccidentOrders` / `selectActionOrders`):
// SR y Order son entidades dobles — el SR tiene sus campos propios
// (name, pkInit/End, concessions/roads/teams, etc.) y la Order asociada
// tiene los campos que viven en su lado (description, notes, specialTag,
// roid, state canónico…).
//
// Para cada SR de la lista, consultamos la Order via `GET_ORDER_BY_ID`
// usando `sr.orderIDs[0]` (invariante: length === 1) y devolvemos un mapa
// `srId → Order` que los loaders pasan al adapter.
// ═══════════════════════════════════════════════════════════════════

export type SurveillanceRouteOrder = {
  id: string;
  description?: string;
  notes?: string;
  specialTag?: string;
  roid?: string;
  state?: string;
  // Fecha de finalización del RV (la escribe finish() en la Order gemela).
  // Alimenta la columna "Fecha final" del listado (paridad #72 con WO).
  registerEndDate?: string | null;
  // Creador del RV (Tipo C exclusivo de la Order gemela; el fragment
  // NEW_ORDER ya lo pide). Alimenta la columna "Creado por" del listado —
  // el adapter lo resuelve a nombre contra filtersDataOptions.users.
  userId?: string | null;
};

export const selectOrdersForSurveillanceRoutes = async (
  surveillanceRoutes: SurveillanceRoute[] = [],
  reFetch = false,
): Promise<Record<string, SurveillanceRouteOrder>> => {
  try {
    // Warm-up #92 aplicado al selector propio del RV (la PR cubría los 5
    // selectors de main; este es solo-SR): UNA llamada `OrdersByIds` puebla la
    // caché normalizada y el per-row de abajo (INTACTO, es el fallback) se
    // sirve con cache-hit. Con reFetch el helper se corta solo (per-row va
    // network-only e ignoraría la caché).
    await warmupOrdersByIds(
      surveillanceRoutes.map((sr) => sr.orderIDs?.[0]),
      reFetch,
    );
    const entries = await Promise.all(
      surveillanceRoutes.map(async (sr) => {
        const orderId = sr.orderIDs?.[0];
        if (!orderId) return [sr.id, undefined] as const;
        // Resiliencia por-fila (#74, patrón selectAllOrders): una Order
        // huérfana/borrada o un error puntual de la query NO debe tumbar el
        // mapa entero (el Promise.all rechazaría y el catch global dejaría
        // TODAS las filas sin su Order — ROIDs en fallback). Skip + warn.
        try {
          const {
            data: { order },
          } = await client.query({
            query: GET_ORDER_BY_ID,
            variables: { id: orderId },
            fetchPolicy: reFetch ? 'network-only' : 'cache-first',
          });
          if (!order) {
            console.warn(
              `[selectOrdersForSurveillanceRoutes] Order ${orderId} del RV ${sr.id} no resuelta — fila con fallback.`,
            );
            return [sr.id, undefined] as const;
          }
          return [sr.id, order as SurveillanceRouteOrder] as const;
        } catch (rowErr) {
          console.warn(
            `[selectOrdersForSurveillanceRoutes] error resolviendo Order ${orderId} del RV ${sr.id} — fila con fallback.`,
            rowErr,
          );
          return [sr.id, undefined] as const;
        }
      }),
    );

    return entries.reduce<Record<string, SurveillanceRouteOrder>>(
      (acc, [srId, order]) => {
        if (order) acc[srId] = order;
        return acc;
      },
      {},
    );
  } catch (err) {
    Sentry.captureException(err);
    return {};
  }
};
